import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ModalButtons } from "../../../shared/component/modal-container/modal-container.component"; import { SharedComponentsModule } from "../../../shared/module/shared-components.module";
import { delay, filter, finalize, map, switchMap, takeUntil, tap } from "rxjs/operators";
import { BoxServiceService } from "../../../box/service/box-service.service";
import { firstValueFrom, Observable, of, Subject, take, timer } from "rxjs";
import { BoxAccessPropsDTO, BoxFileWithMetadataDTO, DetailedBoxNodeInfo } from "../../../box/dto/BoxDTOWrapper";
import { NOTIFICATIONS, NotificationsService } from "../../../../core/service/notifications.service";
import { SpinnerService } from "../../../../core/service/spinner.service";
import { FileDeletionMetricData, KpiModalDocumentCrudWrapper } from "../../dto/box/box-kpi-dtos";
import { ToolTipSettings } from "../../../shared/component/tooltip/tooltip.util";
import { DocumentsAiService } from "../../../box/ai/service/documents-ai.service";
import { AllowedFileTypeUtils } from "../../../box/enums/allowed-file-type";
import { DocumentChatComponent } from "../../../box/ai/document-chat/document-chat.component";
import { TooltipKPIModalDocument } from "../../service/internal/documents-helper.service";
import { DragDropDocumentService } from "./service/drag-and-drop-document.service";
declare let Box;

@Component({
  selector: 'app-kpi-modal-document-crud',
  standalone: true,
  imports: [
    SharedComponentsModule,
    DocumentChatComponent,
  ],
  templateUrl: './kpi-modal-document-crud.component.html',
  styleUrls: ['./kpi-modal-document-crud.component.scss'],
})
// The Box variable is declared outside the component to inform TypeScript that
// it's a globally available object, likely loaded via a script tag in the HTML,
// and not imported directly in the TypeScript module.
// This avoids TypeScript errors when referencing the Box object inside the component.
/* eslint-disable */
export class KpiModalDocumentCrudComponent implements OnInit, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();

  protected readonly MAX_DELETION_MODAL_NAME_WITHOUT_POSTFIX = 35;

  @Input({ required: true }) wrapper: KpiModalDocumentCrudWrapper;
  @Input({ required: true }) isEditEnabled: boolean = false;
  @Input() password: string;
  @Input() token: string;
  @Input({ required: true }) isFromMetricSharedLinkPage: boolean = false;
  @Output() closeEmitter = new EventEmitter<CloseKpiMetricDocumentModalUpdateOutput>();

  private previewContainer: ElementRef;
  @ViewChild('previewContainer', { static: false }) set contentForPreviewContainer(content: ElementRef) {
    if (content) {
     this.previewContainer = content;
      this.initializeModalPreview();
    }
  }

  private uploadContainer: ElementRef;
  @ViewChild('uploadContainer', { static: false }) set contentForUploadContainer(content: ElementRef) {
    if (content) {
      this.uploadContainer = content;
      this.initializeBoxUploader();
    }
  }

  @ViewChild('componentContainer', { static: false }) componentContainer: ElementRef;

  showBoxElements: ShowBoxElements = new ShowBoxElements();
  nodeForDeletionWrapper: NodeForDeletionWrapper = {
    node: null,
    displayName: '',
    showModalDeletion: false,
  };
  isLoadingState: boolean = false;
  boxAccessToken: string;
  rowValue: string;
  preview = new Box.ContentPreview();
  uploader = new Box.ContentUploader();
  fileBoxNodes: DetailedBoxNodeInfo[];
  modalButtons: ModalButtons = {
    close: true,
  };
  toolTipSettings: ToolTipSettings = { show: false };
  toolTipForDocumentName: TooltipKPIModalDocument = {
    show: false,
    showFileName: false,
  };
  isDocumentChatSupported = false;

  constructor(
    private boxService: BoxServiceService,
    private notificationsService: NotificationsService,
    private spinnerService: SpinnerService,
    protected docsAiService: DocumentsAiService,
    private dragDropBoxService: DragDropDocumentService,
  ) {}


  ngOnInit(): void {
    if (this.isFromMetricSharedLinkPage) {
      this.isEditEnabled = this.wrapper.isPeriodRequested;
    }
    this.initialize()
    this.setSubscriptions();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
    this.docsAiService.setSelectedFile(undefined);
    if (this.dragDropBoxService.hasPendingFiles()) {
      this.dragDropBoxService.clearPendingFiles();
    }
  }

  close(): void {
    let result = this.wrapper.boxFolderId;
    for (const node of this.fileBoxNodes) {
      result += '\n' + node.title;
    }
    this.closeEmitter.emit({ rowValue: result, close: true });
  }

  async downloadAll(): Promise<void> {
    const sharedLinkParams = this.isFromMetricSharedLinkPage
      ? { token: this.token, password: this.password }
      : undefined;
    const link =
      await firstValueFrom(this.boxService.downloadMultipleFilesKPI(this.wrapper.companyUUID, this.wrapper.metricDataUUID, sharedLinkParams));
    this.downloadBoxFile(link);
  }

  downloadBoxFile(url: string): void {
    const link = document.createElement('a');
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  previewDocument(node: DetailedBoxNodeInfo): void {
    this.showBoxElements.preview = true;
    const file = new BoxFileWithMetadataDTO();
    file.fileName = node.title;
    file.id = node.id;
    this.docsAiService.setSelectedFile(file);
  }

  onDownloadBoxFile(node: DetailedBoxNodeInfo): void {
    this.downloadBoxFile$(node).pipe(
      takeUntil(this._destroyStream)
    ).subscribe({
      error: () => {
        this.notificationsService.error(NOTIFICATIONS.DOWNLOAD_ERROR);
        this.spinnerService.finishLoadingForComponent(this.componentContainer);
      },
    });
  }

  downloadBoxFile$(file: DetailedBoxNodeInfo): Observable<void> {
    return of(null).pipe(
      tap(() => {
        this.spinnerService.startLoadingForComponent(this.componentContainer);
      }),
      switchMap(() => {
        const url = `https://api.box.com/2.0/files/${file.id}/content?access_token=${this.boxAccessToken}`;
        this.downloadBoxFile(url)
        return of(undefined) as Observable<void>;
      }),
      // Need delay because the browser may ignore multiple rapid clicks on links,
      // treating them as duplicate interactions and preventing some downloads from starting.
      delay(1000),
      finalize(() => {
        this.spinnerService.finishLoadingForComponent(this.componentContainer);
      }),
    );
  }

  openModalDeletion(node: DetailedBoxNodeInfo): void {
    const clonedNode = {...node};
    const titleDisplayName = this.truncateFileNameForDeletionModal(clonedNode);
    this.nodeForDeletionWrapper = {node: clonedNode, displayName: titleDisplayName, showModalDeletion: true, };
  }

  async deleteFile(isConFirmed: boolean): Promise<void> {
    if (!isConFirmed) {
      this.resetDeletionWrapper()
      return;
    }
    const node: DetailedBoxNodeInfo = this.nodeForDeletionWrapper.node;
    const fileDeletionDto = new FileDeletionMetricData(this.wrapper.metricDataUUID, node.id,
      this.wrapper.companyUUID, node.title);
    const sharedLinkParams = this.isFromMetricSharedLinkPage
      ? { token: this.token, password: this.password }
      : undefined;
    await firstValueFrom(this.boxService.deleteItemFromMetricData(fileDeletionDto, sharedLinkParams))
    const idx = this.fileBoxNodes.findIndex((nodeStored) => nodeStored.id === node.id);
    if (idx !== -1) {
      this.fileBoxNodes.splice(idx, 1);
    }
    this.resetDeletionWrapper()
  }

  activateTooltip(id: string, title: string) {
    this.toolTipSettings = {
      show: true,
      coordinates: document.getElementById(id).getBoundingClientRect(),
      title: title,
    };
  }

  /**
   * Activates tooltip for document title.
   * If the text is truncated (overflows its container), shows the full file name
   * with "View" below it. If the text is not truncated, shows only "View document".
   *
   * @param id - Element ID of node line for coordinates
   * @param text - The document/file name text to display
   * @param $index - Index used to find the <span> for checking truncation
   */
  activateTitleNameTooltip(id: string, text: string, $index: number) {
    const element = document.getElementById('node-title' + $index) as HTMLElement;
    const isTextTruncated = element && element.scrollWidth > element.clientWidth;
    this.toolTipForDocumentName = {
      show: true,
      showFileName: isTextTruncated,
      coordinates: document.getElementById(id).getBoundingClientRect(),
      text: text,
      title: 'View document'
    };
  }

  async closeOnlyBoxUploadModal(): Promise<void> {
    this.showBoxElements.upload = false;
    this.docsAiService.setSelectedFile(undefined);
    await this.initialize();
  }

  private resetDeletionWrapper(): void {
    this.nodeForDeletionWrapper.node = null;
    this.nodeForDeletionWrapper.showModalDeletion = false;
  }

  async initialize(): Promise<void> {
    this.isLoadingState = true;
    let accessBoxFileNodesWithProps: BoxKpiAccessAndNodesWrapper;
    if (this.isFromMetricSharedLinkPage) {
      accessBoxFileNodesWithProps = await firstValueFrom(this.boxService.getAccessPropsWithNodesKPISharedLink(
        this.wrapper.companyUUID, this.wrapper.metricDataUUID, this.token, this.password))
    } else {
      accessBoxFileNodesWithProps = await firstValueFrom(this.boxService.getAccessPropsWithNodesKPI(this.wrapper.companyUUID, this.wrapper.metricDataUUID))
    }
    this.boxAccessToken = accessBoxFileNodesWithProps.boxAccessPropsDTO.accessToken;
    this.fileBoxNodes = accessBoxFileNodesWithProps.boxNodeInfoDtoList;
    this.rowValue = accessBoxFileNodesWithProps.rowValue;
    this.closeEmitter.emit({rowValue: this.rowValue, close: false});
    if (this.wrapper.isUploadBoxOnStartNeeded) {
      this.showUploadModal();
    }
    this.isLoadingState = false;
  }

  // we can work with AI chat only from fms/kpi users (not from company request page)
  private setSubscriptions() {
    if (this.isFromMetricSharedLinkPage) {
      this.isDocumentChatSupported = false;
      return;
    }
    this.docsAiService.selectedFile$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((file: BoxFileWithMetadataDTO) => {
        if (!file) {
          return;
        }
        this.isDocumentChatSupported = AllowedFileTypeUtils.isDocumentChatSupported(file.fileName);
      });
  }

  showUploadModal(): void {
    this.showBoxElements.upload = true;
  }

  closePreviewModal() {
    this.showBoxElements.preview = false
    this.docsAiService.setSelectedFile(undefined)
  }

  private initializeBoxUploader(): void {
    this.uploader.show(this.wrapper.boxFolderId, this.boxAccessToken, {
      container: this.uploadContainer.nativeElement,
    });

    this.uploader.on('upload', (data: any) => {
      if (this.wrapper.isUploadBoxOnStartNeeded) {
        this.wrapper.isUploadBoxOnStartNeeded = false;
      }
    });

    this.uploader.on('close', () => {
      this.uploader.hide();
      this.clearPendingFiles();

      if (this.wrapper.isUploadBoxOnStartNeeded) {
        this.closeBoxUploadModalAndMainModal();
        return;
      }
      this.closeOnlyBoxUploadModal();
    });

    // if we have  pending files - try to auto upload them,
    if (this.dragDropBoxService.hasPendingFiles()) {
      this.attemptAutoUpload();
    }
  }

  /**
   * Attempts to automatically upload files that were dragged and dropped onto the component.
   * This method is called after the Box uploader is initialized to programmatically
   * trigger the upload of pending files without user interaction.
   *
   * @description
   * The method performs the following steps:
   * 1. Validates that all pending files are in supported formats
   * 2. Polls for the Box file input element (max 5 seconds)
   * 3. Programmatically sets the files on the input and triggers upload
   * 4. Clears pending files after completion or timeout
   *
   * @remarks
   * - If unsupported files are detected, shows a warning and aborts the upload
   * - Uses RxJS timer to poll every 100ms for the file input element
   * - Automatically stops polling after 5 seconds to prevent infinite loops
   * - The file input may not be immediately available as Box loads asynchronously
   *
   */
  private attemptAutoUpload(): void {
    if (this.dragDropBoxService.hasUnsupportedFiles()) {
      this.notificationsService.error(NOTIFICATIONS.UPLOAD_ERROR);
      this.clearPendingFiles();
      return;
    }
    timer(0, 100).pipe(
      takeUntil(this._destroyStream),
      takeUntil(timer(5000)),
      map(() => this.findFileInput()),
      filter(fileInput => fileInput !== null),
      take(1),
      tap(fileInput => {
        this.uploadFiles(fileInput);
      }),
      // Use finalize to always clear files regardless of completion or error
      finalize(() => {
        this.clearPendingFiles();
      })
    ).subscribe({
      error: (error) => {
        this.notificationsService.error(NOTIFICATIONS.UPLOAD_ERROR);
      }
    });
  }

  private findFileInput(): HTMLInputElement | null {
    const input = this.uploadContainer.nativeElement.querySelector('input[type="file"]:not([disabled])');
    return input as HTMLInputElement;
  }

  /**
   * Programmatically uploads files by setting them on the Box file input element.
   * This method bypasses the native file picker dialog by directly assigning files
   * to the input element and triggering a change event.
   *
   * Note: This approach may not trigger Box's automatic file format conversion
   * for unsupported file types (e.g., .keynote, .numbers, .pages).
   *
   * @param fileInput - The Box uploader's file input element
   */
  private uploadFiles(fileInput: HTMLInputElement): void {
    try {
      const dataTransfer = new DataTransfer();
      const pendingFiles = this.dragDropBoxService.getPendingFiles();

      pendingFiles.forEach(file => {
        dataTransfer.items.add(file);
      });

      // Set files on the input element
      Object.defineProperty(fileInput, 'files', {
        value: dataTransfer.files,
        writable: false,
        configurable: true
      });

      // Trigger change event to initiate Box upload
      fileInput.dispatchEvent(new Event('change', {
        bubbles: true,
        cancelable: true
      }));

      // Don't clear files here - wait for Box's complete event
    } catch (error) {
      // Clear pending files on error to prevent stuck state
      this.clearPendingFiles();
      throw error;
    }
  }

  private clearPendingFiles(): void {
    this.dragDropBoxService.clearPendingFiles();
  }

  /**
   * Closes both the Box uploader modal and the main modal.
   *
   * This method is specifically called when the user opened the main modal
   * from the `wrapper.isUploadBoxOnStartNeeded` state but did not upload any files.
   * In this case, we need to close not only the Box uploader modal but also
   * the main modal that was automatically opened.
   *
   * @description Handles the scenario where:
   * 1. User triggered the main modal via `wrapper.isUploadBoxOnStartNeeded`
   * 2. Box uploader was shown automatically
   * 3. User closed the uploader without uploading any files
   * 4. Both modals need to be closed
   *
   */
  closeBoxUploadModalAndMainModal(): void {
    this.clearPendingFiles();
    this.showBoxElements.upload = false;
    this.docsAiService.setSelectedFile(undefined);
    this.closeEmitter.emit({ rowValue: this.rowValue, close: true });
  }

  private truncateFileNameForDeletionModal(node: DetailedBoxNodeInfo): string {
    let displayName = node.title;
    const postfix = node.title;
    const lastDotIndex = postfix.lastIndexOf('.');

    if (lastDotIndex !== -1) {
      const fileExtension = postfix.substring(lastDotIndex + 1);
      const fileName = postfix.substring(0, lastDotIndex);
      if (fileName.length > this.MAX_DELETION_MODAL_NAME_WITHOUT_POSTFIX) {
        displayName = fileName.substring(0, this.MAX_DELETION_MODAL_NAME_WITHOUT_POSTFIX) + '...' + fileExtension;
      }
    } else {
      if (postfix.length > this.MAX_DELETION_MODAL_NAME_WITHOUT_POSTFIX) {
        displayName = postfix.substring(0, this.MAX_DELETION_MODAL_NAME_WITHOUT_POSTFIX) + '...';
      }
    }
    return displayName;
  }

  private initializeModalPreview() {
    this.preview.show(this.docsAiService.getSelectedFile().id, this.boxAccessToken, {
      container: this.previewContainer.nativeElement,
    });
  }
}

export interface BoxKpiAccessAndNodesWrapper {
  rowValue: string
  boxAccessPropsDTO: BoxAccessPropsDTO,
  boxNodeInfoDtoList: DetailedBoxNodeInfo[]
}

class ShowBoxElements {
  preview = false;
  upload = false;
}

export interface CloseKpiMetricDocumentModalUpdateOutput {
  rowValue: string;
  close: boolean;
}

interface NodeForDeletionWrapper {
  node: DetailedBoxNodeInfo;
  displayName: string,
  showModalDeletion: boolean;
}

